import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import {
  turnosCalendario, turnosPlanificarLote, turnoPlanificadoCrear,
  turnoSetPersonal, turnoCancelar, personalList, destacamentosList,
  type TurnoCal, type Personal, type Destacamento,
} from '../api/seguridad';

const DIAS = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];

/** YYYY-MM-DD en horario local (sin corrimiento UTC). */
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Lunes de la semana que contiene a `d`. */
function lunesDe(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = (x.getDay() + 6) % 7; // 0 = lunes
  x.setDate(x.getDate() - dow);
  return x;
}

function sumarDias(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function hhmm(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T'));
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

export default function Turnos() {
  const user = useAuth((s) => s.user);
  const nav = useNavigate();

  const [destId, setDestId] = useState<number | null>(user?.destacamento_id ?? null);
  const [destacamentos, setDestacamentos] = useState<Destacamento[]>([]);
  const [lunes, setLunes] = useState<Date>(() => lunesDe(new Date()));
  const [turnos, setTurnos] = useState<TurnoCal[]>([]);
  const [roster, setRoster] = useState<Personal[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [modal, setModal] = useState<{ turno: TurnoCal } | null>(null);

  const domingo = useMemo(() => sumarDias(lunes, 6), [lunes]);
  const dias = useMemo(() => Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i)), [lunes]);

  useEffect(() => { if (!destId) destacamentosList().then(setDestacamentos).catch(() => {}); }, [destId]);
  useEffect(() => {
    if (!destId) { setRoster([]); return; }
    personalList(destId).then((r) => setRoster(r.filter((p) => p.activo))).catch(() => {});
  }, [destId]);

  const cargar = useCallback(async () => {
    if (!destId) return;
    setCargando(true);
    try {
      const d = await turnosCalendario(destId, ymd(lunes), ymd(domingo));
      setTurnos(d.turnos); setErr(null);
    } catch (e: any) { setErr(e?.message || 'Error al cargar el calendario'); }
    finally { setCargando(false); }
  }, [destId, lunes, domingo]);

  useEffect(() => { cargar(); }, [cargar]);

  const destNombre = user?.destacamento_nombre ?? destacamentos.find((d) => d.id === destId)?.nombre ?? 'Comisaría';

  function turnoDe(dia: Date, tipo: 'mañana' | 'noche'): TurnoCal | undefined {
    const f = ymd(dia);
    return turnos.find((t) => t.tipo === tipo && (t.fecha_inicio_planificada ?? '').slice(0, 10) === f);
  }

  function flash(m: string) { setMsg(m); setTimeout(() => setMsg(null), 3500); }

  async function planificarSemana() {
    if (!destId) return;
    setCargando(true);
    try {
      const r = await turnosPlanificarLote(destId, ymd(lunes), ymd(domingo));
      flash(r?.message || 'Semana planificada');
      await cargar();
    } catch (e: any) { setErr(e?.message || 'Error al planificar'); }
    finally { setCargando(false); }
  }

  async function crearSlot(dia: Date, tipo: 'mañana' | 'noche') {
    if (!destId) return;
    try {
      const { id } = await turnoPlanificadoCrear(destId, ymd(dia), tipo);
      const d = await turnosCalendario(destId, ymd(lunes), ymd(domingo));
      setTurnos(d.turnos);
      // Buscar por id y, como respaldo, por fecha+tipo (más robusto).
      const f = ymd(dia);
      const nuevo = d.turnos.find((t) => t.id === id)
        ?? d.turnos.find((t) => t.tipo === tipo && (t.fecha_inicio_planificada ?? '').slice(0, 10) === f);
      if (nuevo) setModal({ turno: nuevo });
      else setErr('No se pudo abrir el turno recién creado. Recargá la semana.');
    } catch (e: any) { setErr(e?.message || 'Error al crear el turno'); }
  }

  if (!user) return null;

  const rangoLabel = `${lunes.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })} – ${domingo.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}`;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 shrink-0">
        <button onClick={() => nav('/')} className="text-slate-500 hover:text-slate-900 text-xl leading-none">←</button>
        <div className="flex-1">
          <h1 className="text-slate-900 font-bold leading-tight">Turnos</h1>
          <p className="text-slate-500 text-xs leading-tight">{destNombre}</p>
        </div>
      </header>

      {err && <div className="bg-red-50 border-b border-red-200 text-red-700 text-xs px-4 py-1.5">{err}</div>}
      {msg && <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-700 text-xs px-4 py-1.5">{msg}</div>}

      {!destId && (
        <div className="p-4 max-w-md">
          <label className="block text-sm font-semibold text-slate-700 mb-1">Elegí un destacamento</label>
          <select onChange={(e) => setDestId(Number(e.target.value) || null)} className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm">
            <option value="">— seleccioná —</option>
            {destacamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
        </div>
      )}

      {destId && (
        <>
          {/* Barra: navegación de semana + planificar */}
          <div className="bg-white border-b border-slate-200 px-3 py-2 flex flex-wrap items-center gap-2">
            <button onClick={() => setLunes(sumarDias(lunes, -7))} className="text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5">‹ Semana anterior</button>
            <span className="text-sm font-bold text-slate-800">{rangoLabel}</span>
            <button onClick={() => setLunes(sumarDias(lunes, 7))} className="text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1.5">Semana siguiente ›</button>
            <button onClick={() => setLunes(lunesDe(new Date()))} className="text-xs font-semibold text-brand-600 hover:text-brand-800 px-1 py-1.5">Hoy</button>
            <button onClick={planificarSemana} disabled={cargando} className="ml-auto bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white text-xs font-semibold rounded-lg px-3 py-1.5">
              📅 Planificar 7 días
            </button>
          </div>

          {/* Calendario */}
          <main className="flex-1 overflow-auto p-3">
            <div className="min-w-[720px]">
              {/* Encabezado de días */}
              <div className="grid grid-cols-[64px_repeat(7,1fr)] gap-1.5 mb-1.5">
                <div />
                {dias.map((d, i) => {
                  const esHoy = ymd(d) === ymd(new Date());
                  return (
                    <div key={i} className={`text-center rounded-lg py-1 ${esHoy ? 'bg-brand-50 border border-brand-200' : ''}`}>
                      <div className="text-[10px] font-bold text-slate-500">{DIAS[i]}</div>
                      <div className={`text-sm font-bold ${esHoy ? 'text-brand-700' : 'text-slate-700'}`}>{d.getDate()}</div>
                    </div>
                  );
                })}
              </div>

              {/* Filas: mañana / noche */}
              {([['mañana', '08-20h', '☀️'], ['noche', '20-08h', '🌙']] as const).map(([tipo, etiqueta, icono]) => (
                <div key={tipo} className="grid grid-cols-[64px_repeat(7,1fr)] gap-1.5 mb-1.5">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="text-lg leading-none">{icono}</div>
                    <div className="text-[9px] font-semibold text-slate-500 mt-0.5">{etiqueta}</div>
                  </div>
                  {dias.map((dia, i) => {
                    const t = turnoDe(dia, tipo);
                    return <Celda key={i} turno={t} onCrear={() => crearSlot(dia, tipo)} onAbrir={(tt) => setModal({ turno: tt })} />;
                  })}
                </div>
              ))}

              {/* Leyenda */}
              <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-slate-500">
                <Leyenda color="bg-white border-dashed border-slate-300" txt="+ planificar (sin personal)" />
                <Leyenda color="bg-white border-slate-300" txt="Planificado con personal" />
                <Leyenda color="bg-emerald-100 border-emerald-300" txt="Vigente (en curso)" />
                <Leyenda color="bg-slate-100 border-slate-300" txt="Cerrado (histórico)" />
              </div>
            </div>
          </main>
        </>
      )}

      {modal && (
        <ModalPersonal
          turno={modal.turno}
          roster={roster}
          onClose={() => setModal(null)}
          onGuardado={(m) => { setModal(null); flash(m); cargar(); }}
          onError={(e) => setErr(e)}
        />
      )}
    </div>
  );
}

function Leyenda({ color, txt }: { color: string; txt: string }) {
  return <span className="flex items-center gap-1"><span className={`inline-block w-3 h-3 rounded border ${color}`} />{txt}</span>;
}

function Celda({ turno, onCrear, onAbrir }: {
  turno?: TurnoCal;
  onCrear: () => void;
  onAbrir: (t: TurnoCal) => void;
}) {
  // Un slot sin personal (nunca creado, o planificado y vacío) se ve como
  // "+ planificar", igual que al inicio. Si ya existe el slot vacío, clickearlo
  // abre el modal directo (sin recrear); si no existe, lo crea.
  const vacioPlanificado = !!turno && turno.estado === 'planificado' && turno.personal.length === 0;
  if (!turno || vacioPlanificado) {
    return (
      <button onClick={() => (turno ? onAbrir(turno) : onCrear())}
        className="w-full min-h-[68px] rounded-lg border border-dashed border-slate-300 text-slate-400 hover:border-brand-400 hover:text-brand-500 text-xs">
        + planificar
      </button>
    );
  }
  const n = turno.personal.length;
  const estilo =
    turno.estado === 'activo' ? 'bg-emerald-100 border-emerald-300' :
    turno.estado === 'cerrado' ? 'bg-slate-100 border-slate-300' :
    'bg-white border-slate-300';

  return (
    <button onClick={() => onAbrir(turno)} className={`w-full min-h-[68px] rounded-lg border px-1.5 py-1 text-left ${estilo}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-slate-600">
          {turno.estado === 'activo' ? 'en curso' : turno.estado === 'cerrado' ? 'cerrado' : ''}
        </span>
        {turno.estado === 'cerrado' && turno.fecha_inicio && (
          <span className="text-[9px] text-slate-400">{hhmm(turno.fecha_inicio)}</span>
        )}
      </div>
      {n === 0 ? (
        <span className="text-[11px] text-red-500 italic">sin personal</span>
      ) : (
        <ul className="text-[11px] text-slate-700 leading-tight">
          {turno.personal.slice(0, 3).map((p) => (
            <li key={p.tp_id} className="truncate">· {p.nombre}</li>
          ))}
          {n > 3 && <li className="text-slate-400">+{n - 3} más</li>}
        </ul>
      )}
    </button>
  );
}

function ModalPersonal({ turno, roster, onClose, onGuardado, onError }: {
  turno: TurnoCal;
  roster: Personal[];
  onClose: () => void;
  onGuardado: (msg: string) => void;
  onError: (e: string) => void;
}) {
  const editable = turno.estado === 'planificado';
  const [sel, setSel] = useState<Set<number>>(() => new Set(turno.personal.map((p) => p.personal_id).filter(Boolean)));
  const [guardando, setGuardando] = useState(false);

  const fecha = new Date(turno.fecha_inicio_planificada.replace(' ', 'T'))
    .toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: 'short' });
  const horario = turno.tipo === 'noche' ? '20 a 08 h' : '08 a 20 h';

  function toggle(id: number) {
    setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function guardar() {
    setGuardando(true);
    try {
      await turnoSetPersonal(turno.id, [...sel]);
      onGuardado('Personal actualizado');
    } catch (e: any) { onError(e?.message || 'Error al guardar'); setGuardando(false); }
  }

  async function anular() {
    if (!confirm('¿Anular este turno planificado?')) return;
    setGuardando(true);
    try {
      await turnoCancelar(turno.id);
      onGuardado('Turno anulado');
    } catch (e: any) { onError(e?.message || 'Error al anular'); setGuardando(false); }
  }

  return (
    <div className="fixed inset-0 z-[1300] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200">
          <h2 className="font-bold text-slate-900 capitalize">{fecha}</h2>
          <p className="text-xs text-slate-500">Turno {turno.tipo} · {horario}</p>
          {!editable && (
            <p className="text-[11px] text-amber-600 mt-1">
              {turno.estado === 'activo' ? '🔒 Turno en curso — no se puede modificar el personal.' : '🔒 Turno cerrado — solo lectura (histórico).'}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {editable ? (
            roster.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-6">No hay personal en el destacamento. Cargalo en «Personal».</p>
            ) : (
              <ul className="space-y-1">
                {roster.map((p) => (
                  <li key={p.id}>
                    <label className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} className="accent-brand-600 w-4 h-4" />
                      <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">{p.apellido}, {p.nombre}
                        {p.cargo_nombre && <span className="text-slate-400"> · {p.cargo_nombre}</span>}
                      </span>
                      {p.admin_usuario_id ? (
                        <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700" title="Tiene la app: recibe alertas">📲 recibe</span>
                      ) : (
                        <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400" title="Sin cuenta de app: no recibe alertas">🔕 sin app</span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            )
          ) : (
            turno.personal.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-6">Sin personal registrado en este turno.</p>
            ) : (
              <ul className="space-y-1">
                {turno.personal.map((p) => (
                  <li key={p.tp_id} className="flex items-center justify-between px-2.5 py-2 rounded-lg border border-slate-200">
                    <span className="text-sm text-slate-800">{p.nombre}{p.cargo_nombre && <span className="text-slate-400"> · {p.cargo_nombre}</span>}</span>
                    {p.ingreso_en && <span className="text-[10px] text-slate-400">{hhmm(p.ingreso_en)}{p.egreso_en ? `–${hhmm(p.egreso_en)}` : ''}</span>}
                  </li>
                ))}
              </ul>
            )
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex items-center gap-2">
          {editable && (
            <button onClick={anular} disabled={guardando} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40">Anular turno</button>
          )}
          <button onClick={onClose} className="ml-auto text-sm font-semibold text-slate-500 hover:text-slate-800 px-3 py-2">Cerrar</button>
          {editable && (
            <button onClick={guardar} disabled={guardando} className="bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white text-sm font-semibold rounded-lg px-4 py-2">
              {guardando ? 'Guardando…' : `Guardar (${sel.size})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
